import { createContext, useContext, useMemo, useState, useCallback } from 'react';

const dict = {
  es: {
    nav: {
      services: 'Servicios',
      how: 'Cómo funciona',
      memberships: 'Membresías',
      book: 'Reservar',
      manage: 'Mi suscripción',
    },
    hero: {
      eyebrow: 'Premium Laundry Services',
      h1a: 'Recupera tu',
      h1b: 'tiempo.',
      sub: 'Devuélvete entre 8 y 12 horas al mes sin volver a preocuparte por lavar, secar o doblar ropa.',
      ctaPrimary: 'Reservar pickup',
      ctaSecondary: 'Ver planes',
    },
    services: {
      title: 'Cuatro pasos. Cero esfuerzo.',
      subtitle: 'Diseñado para que tu ropa esté lista, mientras tú vives tu día.',
      items: [
        { key: 'pickup', title: 'Pick up', desc: 'Recogemos donde estés, a la hora que elijas.' },
        { key: 'wash', title: 'Wash', desc: 'Lavado profesional con detergentes premium.' },
        { key: 'fold', title: 'Fold', desc: 'Doblado impecable, listo para guardar.' },
        { key: 'delivery', title: 'Delivery', desc: 'Entregamos puntual, donde lo necesites.' },
      ],
    },
    how: {
      title: 'Cómo funciona',
      subtitle: 'Cuatro pasos pensados para tu rutina.',
      steps: [
        {
          title: 'Agenda en 30 segundos',
          desc: 'Elige el día y la hora que mejor se adapten a tu rutina.',
          bullets: ['Sin llamadas', 'Sin filas', 'Pickup donde estés'],
        },
        {
          title: 'Nosotros hacemos todo el trabajo',
          desc: 'Cada pedido se lava de forma individual con el detergente que tú elijas.',
          bullets: ['Nunca mezclamos tu ropa', 'Lavado profesional', 'Doblado premium'],
        },
        {
          title: 'Recupera hasta 8 horas al mes',
          desc: 'Mientras nosotros cuidamos tu ropa, tú disfrutas de tu tiempo.',
          bullets: ['Trabaja', 'Descansa', 'Vive tu día'],
        },
        {
          title: 'Recíbela lista para guardar',
          desc: 'Tu ropa vuelve limpia, doblada y organizada.',
          bullets: ['Entrega puntual', 'Lista para guardar', 'Garantía de satisfacción'],
        },
      ],
    },
    plans: {
      title: 'Planes que crecen contigo.',
      subtitle: 'Tres membresías. Más valor en cada nivel.',
      perMonth: '/mes',
      mostPopular: 'Más popular',
      select: 'Elegir plan',
    },
    booking: {
      title: 'Reserva tu pickup.',
      subtitle: 'Completa el formulario y te confirmaremos tu hora.',
      fullName: 'Nombre completo',
      email: 'Email',
      phone: 'Teléfono',
      pickup: 'Dirección de recogida',
      delivery: 'Dirección de entrega',
      date: 'Fecha de pickup',
      pickDate: 'Seleccionar fecha',
      time: 'Hora de pickup',
      pickTime: 'Seleccionar hora',
      plan: 'Plan',
      pickPlan: 'Elige tu plan',
      detergent: 'Tipo de detergente',
      pickDetergent: 'Elige el detergente',
      detergentStandard: 'Detergente Estándar',
      detergentHypo: 'Detergente Hipoalergénico',
      extrasTitle: 'Extras',
      extrasSubtitle: 'Añade servicios adicionales a tu pickup (opcional).',
      extrasEmpty: 'Actualmente no hay extras disponibles.',
      extrasEndsIn: (n) => (n <= 0 ? 'Termina hoy' : n === 1 ? 'Termina en 1 día' : `Termina en ${n} días`),
      extrasEndsSoon: 'Termina pronto',
      totalLabel: 'Total',
      notes: 'Notas (opcional)',
      submit: 'Confirmar reserva',
      sending: 'Enviando…',
      payCard: 'Pagar con tarjeta',
      payPaypal: 'Pagar con PayPal',
      orPay: 'o',
      paySubtitle: 'Pago seguro. Cancela cuando quieras.',
      successTitle: '¡Reserva confirmada!',
      successDesc: 'Te enviaremos un email y SMS de confirmación con los detalles de tu pickup.',
      successCta: 'Listo',
      errorTitle: 'No pudimos enviar tu reserva',
      errorDesc: 'Revisa los campos e inténtalo de nuevo.',
      redirecting: 'Redirigiendo a pago seguro…',
    },
    footer: {
      tagline: 'Lavandería premium a domicilio.',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.',
    },
    payment: {
      successTitle: '¡Pago confirmado!',
      successDesc: 'Tu suscripción está activa. Te enviaremos un email y SMS con los detalles de tu primer pickup.',
      successCta: 'Volver al inicio',
      cancelTitle: 'Pago cancelado',
      cancelDesc: 'No te preocupes — no se te cobró nada. Puedes volver a intentarlo cuando quieras.',
      cancelCta: 'Volver al inicio',
      processing: 'Confirmando tu pago…',
      failedTitle: 'No pudimos confirmar tu pago',
      failedDesc: 'Si crees que es un error, contáctanos.',
    },
    portal: {
      title: 'Gestiona tu suscripción',
      desc: 'Ingresa el email con el que pagaste. Te llevamos al portal seguro de Stripe para ver, pausar o cancelar tu plan.',
      emailLabel: 'Email',
      open: 'Abrir portal',
      opening: 'Abriendo…',
      notFound: 'No encontramos una suscripción activa con ese email.',
      cancel: 'Cancelar',
    },
  },
  en: {
    nav: {
      services: 'Services',
      how: 'How it works',
      memberships: 'Memberships',
      book: 'Book now',
      manage: 'My subscription',
    },
    hero: {
      eyebrow: 'Premium Laundry Services',
      h1a: 'Get your',
      h1b: 'time back.',
      sub: 'Reclaim 8–12 hours a month and stop worrying about washing, drying or folding laundry ever again.',
      ctaPrimary: 'Book a pickup',
      ctaSecondary: 'See plans',
    },
    services: {
      title: 'Four steps. Zero effort.',
      subtitle: 'Designed so your laundry is ready while you live your day.',
      items: [
        { key: 'pickup', title: 'Pick up', desc: 'We pick up wherever you are, whenever you want.' },
        { key: 'wash', title: 'Wash', desc: 'Professional wash with premium detergents.' },
        { key: 'fold', title: 'Fold', desc: 'Crisp folding, ready to store.' },
        { key: 'delivery', title: 'Delivery', desc: 'On-time delivery, exactly where you need.' },
      ],
    },
    how: {
      title: 'How it works',
      subtitle: 'Four steps built for your routine.',
      steps: [
        {
          title: 'Book in 30 seconds',
          desc: 'Pick the day and time that fit your routine.',
          bullets: ['No phone calls', 'No lines', 'Pickup wherever you are'],
        },
        {
          title: 'We do all the work',
          desc: 'Each order is washed individually with the detergent you choose.',
          bullets: ['We never mix your laundry', 'Professional wash', 'Premium folding'],
        },
        {
          title: 'Get up to 8 hours back a month',
          desc: 'While we take care of your laundry, you enjoy your time.',
          bullets: ['Work', 'Rest', 'Live your day'],
        },
        {
          title: 'Ready to put away',
          desc: 'Your laundry comes back clean, folded and organized.',
          bullets: ['On-time delivery', 'Ready to store', 'Satisfaction guarantee'],
        },
      ],
    },
    plans: {
      title: 'Plans that grow with you.',
      subtitle: 'Three memberships. More value at every level.',
      perMonth: '/month',
      mostPopular: 'Most popular',
      select: 'Choose plan',
    },
    booking: {
      title: 'Book your pickup.',
      subtitle: 'Fill in the form and we will confirm your time slot.',
      fullName: 'Full name',
      email: 'Email',
      phone: 'Phone',
      pickup: 'Pickup address',
      delivery: 'Delivery address',
      date: 'Pickup date',
      pickDate: 'Select date',
      time: 'Pickup time',
      pickTime: 'Select time',
      plan: 'Plan',
      pickPlan: 'Choose your plan',
      detergent: 'Detergent type',
      pickDetergent: 'Choose detergent',
      detergentStandard: 'Standard Detergent',
      detergentHypo: 'Hypoallergenic Detergent',
      extrasTitle: 'Extras',
      extrasSubtitle: 'Add optional services to your pickup.',
      extrasEmpty: 'No extras available right now.',
      extrasEndsIn: (n) => (n <= 0 ? 'Ends today' : n === 1 ? 'Ends in 1 day' : `Ends in ${n} days`),
      extrasEndsSoon: 'Ending soon',
      totalLabel: 'Total',
      notes: 'Notes (optional)',
      submit: 'Confirm booking',
      sending: 'Sending…',
      payCard: 'Pay with card',
      payPaypal: 'Pay with PayPal',
      orPay: 'or',
      paySubtitle: 'Secure payment. Cancel any time.',
      successTitle: 'Booking confirmed!',
      successDesc: 'We will email and SMS you a confirmation with your pickup details.',
      successCta: 'Done',
      errorTitle: 'We could not submit your booking',
      errorDesc: 'Please check the fields and try again.',
      redirecting: 'Redirecting to secure payment…',
    },
    footer: {
      tagline: 'Premium home laundry service.',
      contact: 'Contact',
      rights: 'All rights reserved.',
    },
    payment: {
      successTitle: 'Payment confirmed!',
      successDesc: 'Your subscription is active. We will email and SMS you the details of your first pickup.',
      successCta: 'Back to home',
      cancelTitle: 'Payment cancelled',
      cancelDesc: "Don't worry — you were not charged. You can try again any time.",
      cancelCta: 'Back to home',
      processing: 'Confirming your payment…',
      failedTitle: 'We could not confirm your payment',
      failedDesc: 'If you think this is a mistake, please contact us.',
    },
    portal: {
      title: 'Manage your subscription',
      desc: 'Enter the email you paid with. We will take you to the secure Stripe portal to view, pause or cancel your plan.',
      emailLabel: 'Email',
      open: 'Open portal',
      opening: 'Opening…',
      notFound: 'No active subscription found for that email.',
      cancel: 'Cancel',
    },
  },
};

const I18nCtx = createContext({ lang: 'es', t: dict.es, setLang: () => {} });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState('es');
  const toggle = useCallback(() => setLang((l) => (l === 'es' ? 'en' : 'es')), []);
  const value = useMemo(
    () => ({ lang, t: dict[lang], setLang, toggle }),
    [lang]
  );
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
};

export const useI18n = () => useContext(I18nCtx);
